import React, { Component, ErrorInfo } from 'react';
import { Button } from './ui/Button';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error);
    console.error('Error info:', errorInfo);
    this.setState({ errorInfo });
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleClearLocalStorage = () => {
    localStorage.clear();
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full p-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <div className="space-y-4">
              <Button
                onClick={this.handleRefresh}
                variant="primary"
                className="w-full"
              >
                Refresh Page
              </Button>
              <Button
                onClick={this.handleClearLocalStorage}
                variant="secondary"
                className="w-full"
              >
                Clear Data & Refresh
              </Button>
            </div>
            <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded text-left">
              <h3 className="font-bold text-sm mb-2">Error Details:</h3>
              <pre className="text-xs overflow-auto whitespace-pre-wrap max-h-64">
                {this.state.error?.stack}
              </pre>
            </div>
            {this.state.errorInfo && (
              <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded text-left">
                <h3 className="font-bold text-sm mb-2">Component Stack:</h3>
                <pre className="text-xs overflow-auto whitespace-pre-wrap max-h-64">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 